/**
 * TaskDetailPanel — slide-in side panel for task editing + subtasks/dependencies.
 *
 * Pure presentational: caller supplies task + handlers.
 * Light theme only.
 */
import { useEffect, useState } from "react";
import { CaPhoneTaskPanel, EXECUTION_VIEWS } from "./CaPhoneTaskPanel";
import { TaskComments } from "./TaskComments";
import type { TaskApi } from "./api";

import {
  formatDate,
  formatDateTime,
  isOverdue,
  priorityChipClass,
  statusChipClass,
} from "./format";
import {
  PRIORITY_LABELS_JA,
  STATUS_LABELS_JA,
  TASK_PRIORITIES,
  TASK_STATUSES,
} from "./types";
import type {
  Task,
  TaskDependency,
  TaskPriority,
  TaskStatus,
  UpdateTaskInput,
} from "./types";

export interface TaskDetailPanelProps {
  task: Task | null;
  members?: { id: string; displayName: string }[];
  subtasks?: Task[];
  blockedBy?: TaskDependency[];
  blocks?: TaskDependency[];
  onClose?: () => void;
  onUpdate?: (id: string, patch: UpdateTaskInput) => void;
  onDelete?: (id: string) => void;
  onAddSubtask?: (parentId: string, title: string) => void;
  onSuggestDue?: (id: string) => Promise<{ suggested_due_at: number; rationale: string } | null>;
  onExecutionRecorded?: (id: string) => Promise<void>;
  commentsApi?: TaskApi;
  canEdit?: boolean;
  canAssign?: boolean;
  canUpdateStatus?: boolean;
}

function inputDateToMillis(s: string): number | null {
  if (!s) return null;
  const d = new Date(s + "T00:00:00");
  return isNaN(d.getTime()) ? null : d.getTime();
}

function millisToInputDate(ms: number | null): string {
  if (!ms) return "";
  const d = new Date(ms);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function TaskDetailPanel({
  task,
  members = [],
  subtasks = [],
  blockedBy = [],
  blocks = [],
  onClose,
  onUpdate,
  onDelete,
  onAddSubtask,
  onSuggestDue,
  onExecutionRecorded,
  commentsApi,
  canEdit = true,
  canAssign = true,
  canUpdateStatus = true,
}: TaskDetailPanelProps) {
  const [title, setTitle] = useState(task?.title ?? "");
  const [description, setDescription] = useState(task?.description ?? "");
  const [subTitle, setSubTitle] = useState("");
  const [aiSuggestion, setAiSuggestion] = useState<{
    suggested_due_at: number;
    rationale: string;
  } | null>(null);

  useEffect(() => {
    setTitle(task?.title ?? "");
    setDescription(task?.description ?? "");
    setSubTitle("");
    setAiSuggestion(null);
  }, [task?.id]);

  // Esc to close
  useEffect(() => {
    if (!task) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") {
        onClose?.();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [task, onClose]);

  if (!task) return null;

  const overdue = isOverdue(task);
  const subProgress = subtasks.length > 0
    ? Math.round(
        (subtasks.filter((s) => s.status === "done").length / subtasks.length) *
          100,
      )
    : 0;

  return (
    <aside
      role="complementary"
      aria-label="タスク詳細"
      data-testid="task-detail"
      className={`fixed inset-y-0 right-0 z-30 flex w-full flex-col gap-3 overflow-y-auto bg-canvas p-5 shadow-lg ring-1 ring-border-subtle ${task.execution ? "max-w-5xl md:w-[min(960px,95vw)]" : "max-w-md md:w-[440px]"}`}
    >
      <header className="flex items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase ${priorityChipClass(task.priority)}`}
          >
            {task.priority}
          </span>
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${statusChipClass(task.status)}`}
          >
            {STATUS_LABELS_JA[task.status]}
          </span>
          {overdue && (
            <span className="inline-flex items-center gap-1 rounded-full bg-danger-bg px-2 py-0.5 text-[11px] font-semibold text-danger">
              ⚠ 期限超過
            </span>
          )}
        </div>
        <button
          type="button"
          aria-label="詳細を閉じる"
          onClick={onClose}
          className="rounded-md border border-border-default bg-canvas px-2 py-1 text-xs text-fg-secondary hover:bg-surface-raised"
        >
          ✕
        </button>
      </header>

      {task.execution && onExecutionRecorded && (
        <CaPhoneTaskPanel task={task} onRecorded={onExecutionRecorded} />
      )}

      {commentsApi && <TaskComments key={task.id} task={task} api={commentsApi} members={members} canPost={canUpdateStatus} />}

      <label className="flex flex-col gap-1 text-xs text-fg-muted">
        タイトル
        <input
          type="text"
          disabled={!canEdit}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={() => {
            if (title.trim() && title !== task.title) {
              onUpdate?.(task.id, { title: title.trim() });
            }
          }}
          className="rounded-md border border-border-default bg-canvas px-3 py-2 text-sm font-medium text-fg-primary focus-visible:shadow-focus"
        />
      </label>

      <label className="flex flex-col gap-1 text-xs text-fg-muted">
        詳細 (Markdown)
        <textarea
          disabled={!canEdit}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onBlur={() => {
            if (description !== task.description) {
              onUpdate?.(task.id, { description });
            }
          }}
          rows={5}
          placeholder="背景・前提・受け入れ条件など"
          className="rounded-md border border-border-default bg-canvas px-3 py-2 text-sm text-fg-primary focus-visible:shadow-focus"
        />
      </label>

      <div className="grid grid-cols-2 gap-3">
        <label className="flex flex-col gap-1 text-xs text-fg-muted">
          ステータス
          <select
            value={task.status}
            disabled={!canUpdateStatus || !!task.execution}
            onChange={(e) =>
              onUpdate?.(task.id, { status: e.target.value as TaskStatus })
            }
            className="rounded-md border border-border-default bg-canvas px-2 py-1.5 text-sm focus-visible:shadow-focus"
          >
            {TASK_STATUSES.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS_JA[s]}
              </option>
            ))}
          </select>
          {task.execution && <span>{EXECUTION_VIEWS[task.execution.kind]?.statusNote}</span>}
        </label>
        <label className="flex flex-col gap-1 text-xs text-fg-muted">
          優先度
          <select
            value={task.priority}
            disabled={!canEdit}
            onChange={(e) =>
              onUpdate?.(task.id, { priority: e.target.value as TaskPriority })
            }
            className="rounded-md border border-border-default bg-canvas px-2 py-1.5 text-sm focus-visible:shadow-focus"
          >
            {TASK_PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {PRIORITY_LABELS_JA[p]}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="flex flex-col gap-1 text-xs text-fg-muted">
          期限
          <input
            type="date"
            disabled={!canEdit}
            value={millisToInputDate(task.due_at)}
            onChange={(e) =>
              onUpdate?.(task.id, { due_at: inputDateToMillis(e.target.value) })
            }
            className="rounded-md border border-border-default bg-canvas px-2 py-1.5 text-sm focus-visible:shadow-focus"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-fg-muted">
          担当者
          <select disabled={!canAssign} value={task.assignee || ""} onChange={e => onUpdate?.(task.id, { assignee: e.target.value || null })} className="rounded-md border border-border-default bg-canvas px-2 py-1.5 text-sm focus-visible:shadow-focus disabled:opacity-60"><option value="">未割り当て</option>{task.assignee && !members.some(member => member.id === task.assignee) && <option value={task.assignee}>{task.assignee}</option>}{members.map(member => <option key={member.id} value={member.id}>{member.displayName}</option>)}</select>
          {!canAssign && <span>担当者の変更はタスク管理者が行います。</span>}
        </label>
      </div>

      {onSuggestDue && (
        <div className="rounded-md border border-brand-100 bg-brand-50/40 p-3">
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center gap-1 text-xs font-medium text-brand-700">
              <svg
                aria-hidden
                width={12}
                height={12}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
              </svg>
              AI 期限提案
            </span>
            <button
              type="button"
              onClick={async () => {
                const suggestion = await onSuggestDue(task.id);
                if (suggestion) setAiSuggestion(suggestion);
              }}
              className="rounded-md border border-border-default bg-canvas px-2 py-1 text-xs text-fg-secondary hover:bg-surface-raised"
            >
              提案を依頼
            </button>
          </div>
          {aiSuggestion && (
            <p className="mt-2 text-xs text-fg-muted">
              提案期限:{" "}
              <span className="font-medium text-fg-primary">
                {formatDate(aiSuggestion.suggested_due_at)}
              </span>
              <br />
              <span className="text-fg-subtle">{aiSuggestion.rationale}</span>
              <button
                type="button"
                className="ml-2 font-medium text-brand-700 underline hover:text-brand-900"
                onClick={() =>
                  onUpdate?.(task.id, { due_at: aiSuggestion.suggested_due_at })
                }
              >
                適用
              </button>
            </p>
          )}
        </div>
      )}

      {/* Subtasks */}
      <section
        data-testid="subtask-section"
        className="rounded-md bg-surface-base p-3 ring-1 ring-border-subtle"
      >
        <div className="mb-2 flex items-center justify-between">
          <h4 className="text-xs font-semibold text-fg-secondary">
            サブタスク ({subtasks.length})
          </h4>
          {subtasks.length > 0 && (
            <span className="text-[11px] text-fg-muted">
              完了率 {subProgress}%
            </span>
          )}
        </div>
        {subtasks.length > 0 && (
          <div className="mb-2 h-1.5 overflow-hidden rounded-full bg-surface-raised">
            <div
              className="h-full bg-emerald-500 transition-all"
              style={{ width: `${subProgress}%` }}
            />
          </div>
        )}
        <ul className="mb-2 flex flex-col gap-1">
          {subtasks.map((s) => (
            <li
              key={s.id}
              className="flex items-center justify-between gap-2 rounded px-1.5 py-1 text-xs text-fg-primary hover:bg-canvas"
            >
              <span className="flex min-w-0 items-center gap-2">
                <span
                  className={`inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ${statusChipClass(s.status)}`}
                >
                  {STATUS_LABELS_JA[s.status]}
                </span>
                <span className="truncate">{s.title}</span>
              </span>
              <span className="shrink-0 text-fg-muted">{formatDate(s.due_at)}</span>
            </li>
          ))}
          {subtasks.length === 0 && (
            <li className="text-xs text-fg-muted">サブタスクはありません</li>
          )}
        </ul>
        {onAddSubtask && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const v = subTitle.trim();
              if (!v) return;
              onAddSubtask(task.id, v);
              setSubTitle("");
            }}
            className="flex gap-2"
          >
            <input
              type="text"
              value={subTitle}
              onChange={(e) => setSubTitle(e.target.value)}
              placeholder="サブタスクを追加"
              className="grow rounded-md border border-border-default bg-canvas px-2 py-1 text-xs focus-visible:shadow-focus"
            />
            <button
              type="submit"
              className="rounded-md bg-brand-600 px-3 py-1 text-xs font-medium text-fg-inverse hover:bg-brand-700"
            >
              追加
            </button>
          </form>
        )}
      </section>

      {/* Dependencies */}
      {(blockedBy.length > 0 || blocks.length > 0) && (
        <section className="rounded-md bg-surface-base p-3 ring-1 ring-border-subtle">
          <h4 className="mb-2 text-xs font-semibold text-fg-secondary">
            依存関係
          </h4>
          {blockedBy.length > 0 && (
            <p className="text-xs text-fg-primary">
              ブロック中:{" "}
              <span className="text-danger">
                {blockedBy.map((d) => d.predecessor_id).join(", ")}
              </span>
            </p>
          )}
          {blocks.length > 0 && (
            <p className="mt-1 text-xs text-fg-primary">
              ブロック先: {blocks.map((d) => d.successor_id).join(", ")}
            </p>
          )}
        </section>
      )}

      {/* Tags */}
      {task.tags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {task.tags.map((tag) => (
            <span
              key={tag}
              className="rounded bg-surface-base px-2 py-0.5 text-[11px] text-fg-secondary ring-1 ring-border-subtle"
            >
              #{tag}
            </span>
          ))}
        </div>
      )}

      <p className="text-[11px] text-fg-subtle">
        作成: {formatDateTime(task.created_at)} · 更新:{" "}
        {formatDateTime(task.updated_at)}
        {task.completed_at !== null && (
          <>
            {" · "}完了: {formatDateTime(task.completed_at)}
          </>
        )}
      </p>

      {onDelete && (
        <button
          type="button"
          onClick={() => {
            if (window.confirm(`タスク "${task.title}" を削除しますか?`)) {
              onDelete(task.id);
            }
          }}
          className="mt-auto rounded-md border border-danger/40 bg-danger-bg px-3 py-1.5 text-sm font-medium text-danger transition-colors hover:bg-danger hover:text-fg-inverse"
        >
          このタスクを削除
        </button>
      )}
    </aside>
  );
}
